<!-- CertStatsSection — 인증서 목록 페이지 상단 통계 섹션 (필터 패널보다 위, 첫 번째 섹션).
     GET /products/admin/dentium/china/certifications/stats 를 자체 조회한다(페이지와 독립적인
     axios 인스턴스 구성 — CertificateList.vue 와 동일 패턴). 백엔드가 아직 배포되지 않아 404가
     나더라도(또는 그 외 어떤 에러든) 화면 자체가 죽지 않도록 실패 시 아무것도 렌더링하지 않는다. -->
<template>
  <a-card v-if="stats && !failed" class="certstats-card" :bordered="true">
    <div class="certstats-label">{{ $t('certstats_title') }}</div>

    <!-- 통계 타일 5개: 오늘 / 최근 7일 / 이번 달 / 지난달 / 누적 -->
    <div class="certstats-tiles">
      <div v-for="tile in tiles" :key="tile.key" class="certstats-tile">
        <div class="certstats-tile-label">{{ $t(tile.labelKey) }}</div>
        <div class="certstats-tile-value">{{ formatNumber(tile.certs) }}</div>
      </div>
    </div>

    <!-- 일별 14일 / 월별 6개월 미니 바 차트 -->
    <div class="certstats-charts">
      <div class="certstats-chart">
        <div class="certstats-chart-title">{{ $t('certstats_daily_chart_title') }}</div>
        <div class="certstats-bars">
          <a-tooltip
            v-for="(d, idx) in dailyData"
            :key="`daily-${idx}`"
            :title="dailyTooltip(d)"
          >
            <div
              class="certstats-bar"
              :style="{ height: barHeight(d.certs, dailyMax) }"
            />
          </a-tooltip>
        </div>
      </div>

      <div class="certstats-chart">
        <div class="certstats-chart-title">{{ $t('certstats_monthly_chart_title') }}</div>
        <div class="certstats-bars">
          <a-tooltip
            v-for="(m, idx) in monthlyData"
            :key="`monthly-${idx}`"
            :title="monthlyTooltip(m)"
          >
            <div
              class="certstats-bar certstats-bar-monthly"
              :style="{ height: barHeight(m.certs, monthlyMax) }"
            />
          </a-tooltip>
        </div>
        <div class="certstats-month-labels">
          <span v-for="(m, idx) in monthlyData" :key="`monthly-label-${idx}`" class="certstats-month-label">
            {{ monthShort(m.month) }}
          </span>
        </div>
      </div>
    </div>

    <div class="certstats-caption">{{ $t('certstats_timezone_caption') }}</div>
  </a-card>
</template>

<script>
// @MX:NOTE: [AUTO] 백엔드 EP(/products/admin/dentium/china/certifications/stats)가 병행 구현
//   중이라 배포 전 환경에서는 404가 정상 상황이다. 그 어떤 에러도 화면을 깨뜨리지 않고 섹션
//   자체를 접어야(렌더하지 않아야) FE 를 BE 보다 먼저 안전하게 배포할 수 있다.
import certificateApis from '../api';
import chinaInapi from '@/apis/chinaInapi';

export default {
  name: 'CertStatsSection',

  props: {
    manufacturerId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      apis: null,
      loading: false,
      stats: null,
      failed: false,
    };
  },

  computed: {
    tiles() {
      if (!this.stats) return [];
      // 제품(시리얼) 수치는 화면에서 쓰지 않는다 — 인증서 건수만 노출한다(2026-08-20 운영 요청).
      // 백엔드 응답의 serials 는 그대로 두되 소비하지 않는다.
      const pick = (key) => this.stats[key] || { certs: 0 };
      return [
        { key: 'today', labelKey: 'certstats_tile_today', ...pick('today') },
        { key: 'last7Days', labelKey: 'certstats_tile_last7days', ...pick('last7Days') },
        { key: 'thisMonth', labelKey: 'certstats_tile_thismonth', ...pick('thisMonth') },
        { key: 'prevMonth', labelKey: 'certstats_tile_prevmonth', ...pick('prevMonth') },
        { key: 'total', labelKey: 'certstats_tile_total', ...pick('total') },
      ];
    },

    dailyData() {
      return (this.stats && Array.isArray(this.stats.daily)) ? this.stats.daily : [];
    },

    monthlyData() {
      return (this.stats && Array.isArray(this.stats.monthly)) ? this.stats.monthly : [];
    },

    dailyMax() {
      return Math.max(1, ...this.dailyData.map((d) => d.certs || 0));
    },

    monthlyMax() {
      return Math.max(1, ...this.monthlyData.map((m) => m.certs || 0));
    },
  },

  mounted() {
    this.apis = certificateApis(chinaInapi);
    this.fetchStats();
  },

  watch: {
    // XINLAIAN 운영자가 제조사 필터를 바꾸면 통계도 그 범위로 좁아진다.
    // 제조사 계정 로그인 시 manufacturerId 는 항상 null 이므로 refetch 는 일어나지 않는다.
    manufacturerId() {
      this.fetchStats();
    },
  },

  methods: {
    async fetchStats() {
      this.loading = true;
      this.failed = false;
      try {
        const params = {};
        if (this.manufacturerId) params.manufacturerId = this.manufacturerId;
        const response = await this.apis.getCertificateStats(params);
        if (response && response.data) {
          this.stats = response.data;
        } else {
          this.stats = null;
          this.failed = true;
        }
      } catch (error) {
        // 배포 전 환경의 404 포함, 그 어떤 에러든 섹션을 조용히 접는다(no dialog).
        console.error('Error fetching certificate stats:', error);
        this.stats = null;
        this.failed = true;
      } finally {
        this.loading = false;
      }
    },

    formatNumber(n) {
      return (n || 0).toLocaleString();
    },

    barHeight(certs, max) {
      const px = max > 0 ? ((certs || 0) / max) * 60 : 0;
      return `${Math.max(px, 2)}px`;
    },

    monthShort(month) {
      if (!month) return '';
      const parts = month.split('-');
      return parts.length === 2 ? parts[1] : month;
    },

    dailyTooltip(d) {
      return `${d.date} · ${this.$t('certstats_tooltip_certs', { n: this.formatNumber(d.certs) })}`;
    },

    monthlyTooltip(m) {
      return `${m.month} · ${this.$t('certstats_tooltip_certs', { n: this.formatNumber(m.certs) })}`;
    },
  },
};
</script>

<style scoped>
.certstats-card {
  border: 1px solid #f0f0f0;
  margin-bottom: 16px;
}

.certstats-label {
  font-size: 12px;
  font-weight: 600;
  color: #595959;
  background: #fafafa;
  border-left: 3px solid #1677ff;
  padding: 1px 6px;
  border-radius: 0 2px 2px 0;
  white-space: nowrap;
  display: inline-block;
  margin-bottom: 12px;
}

.certstats-tiles {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.certstats-tile {
  flex: 1 1 140px;
  min-width: 120px;
  text-align: center;
  padding: 8px 4px;
}

.certstats-tile-label {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 4px;
  white-space: nowrap;
}

.certstats-tile-value {
  font-size: 24px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #262626;
  line-height: 1.3;
}

.certstats-charts {
  display: flex;
  gap: 16px;
  margin-top: 16px;
}

.certstats-chart {
  flex: 1 1 0;
  min-width: 0;
}

.certstats-chart-title {
  font-size: 12px;
  color: #595959;
  margin-bottom: 8px;
}

.certstats-bars {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 60px;
}

.certstats-bar {
  flex: 1 1 0;
  min-width: 4px;
  background: #1677ff;
  border-radius: 2px 2px 0 0;
}

.certstats-bar-monthly {
  background: #52c41a;
}

.certstats-month-labels {
  display: flex;
  gap: 4px;
  margin-top: 4px;
}

.certstats-month-label {
  flex: 1 1 0;
  text-align: center;
  font-size: 11px;
  color: #8c8c8c;
}

.certstats-caption {
  margin-top: 12px;
  font-size: 12px;
  color: #bfbfbf;
}

@media screen and (max-width: 767px) {
  .certstats-charts {
    flex-direction: column;
  }
}
</style>
